// src/quiz/gre-mock2/verbalData.js
// GRE Mock 2 — Verbal Reasoning.
// Section 1: questions 1–12 (18 min). Section 2: questions 13–27 (23 min).
// Question format: see ../greQuestionTypes.js

const FOREST_PASSAGE =
  "For much of the twentieth century, ecologists treated forest succession as a predictable march toward a stable \"climax\" community, a final assemblage of species determined chiefly by regional climate. Disturbances such as fire or windstorm were regarded as interruptions that merely reset the clock. Beginning in the 1970s, however, field studies increasingly showed that disturbance is not an aberration but an integral part of many forest systems. In some pine forests, for instance, periodic low-intensity fires remove competing hardwood seedlings and release seeds from serotinous cones, which open only under intense heat. Suppressing such fires does not preserve the forest; it transforms it, often allowing dense undergrowth to accumulate until a far more destructive fire occurs.\n\n" +
  "This shift has practical consequences. Land managers who once measured success by how long they kept fire out of a forest now often set deliberate, controlled burns. Critics note that controlled burns carry their own risks and that the historical frequency of fire in a given forest is frequently uncertain. Yet few ecologists today would defend the older view that an undisturbed forest is necessarily a healthy one.";

const PRESS_PASSAGE =
  "Economic historians have often credited the spread of the printing press in fifteenth-century Europe with accelerating literacy. Yet literacy rates in several regions rose substantially before presses were widely available, driven in part by the growth of commerce, which rewarded merchants able to keep written accounts. This chronology suggests that the press may have responded to an existing demand for reading material as much as it created one. None of this diminishes the press's significance: a technology that lowers the cost of books can sustain and broaden a trend it did not begin.";

const BEE_PASSAGE =
  "Honeybee colonies regulate the temperature of their brood nest with remarkable precision, typically holding it between 34 and 36 degrees Celsius. Workers raise the temperature by contracting their flight muscles without moving their wings, and lower it by fanning or by spreading water droplets that cool the comb as they evaporate. Researchers long assumed that this regulation simply protected developing larvae from lethal extremes. Experiments in which pupae were reared at slightly different temperatures within the normal range, however, revealed subtler effects: bees raised near the lower end of the range performed more poorly on learning tasks as adults and were less effective at communicating the location of food through the waggle dance.\n\n" +
  "These findings suggest that thermoregulation does more than keep the brood alive; it shapes the abilities of the adult workforce. Some researchers have gone further, proposing that colonies might adjust brood temperature to produce workers suited to different tasks. That hypothesis remains speculative, since no study has yet shown that colonies vary nest temperature in response to their changing needs.";

const LIGHTING_PASSAGE =
  "In the five years since the city of Larkfield installed brighter streetlights in its downtown district, reported nighttime burglaries there have fallen by 30 percent. City officials conclude that the brighter lighting deterred burglars and plan to install similar lighting in residential neighborhoods.";

const LETTERS_PASSAGE =
  "Literary scholars sometimes treat a writer's letters as a transparent window onto the writer's intentions. But letters, too, are composed for an audience, and a novelist writing to a publisher, a rival, or a friend may adopt a persona as carefully constructed as any fictional narrator's. To read letters as unmediated confession is therefore to ignore the very artistry that makes their authors worth studying.";

const verbalQuestions = [

  // ---------- SECTION 1 ----------

  {
    id: 1,
    question: "Far from being the ______ figure her detractors portrayed, the senator was known among colleagues for her willingness to broker compromises across party lines.",
    options: ["intransigent", "conciliatory", "magnanimous", "pragmatic", "affable"],
    answer: "intransigent",
    explanation: "\"Far from\" sets up a contrast with her willingness to compromise, so the blank needs a word meaning unwilling to compromise: intransigent. Conciliatory and pragmatic describe what she actually was.",
  },
  {
    id: 2,
    type: "blanks",
    question: "Although the novelist's early works were (i)______ by critics as derivative, her later books, which drew on her own idiosyncratic experiences, were praised for their (ii)______.",
    blanks: [
      { options: ["dismissed", "lauded", "ignored"] },
      { options: ["originality", "conventionality", "brevity"] },
    ],
    answer: ["dismissed", "originality"],
    explanation: "\"As derivative\" is a negative judgment, so critics dismissed the early works. \"Although\" signals that the later books earned the opposite verdict — praise for originality, which follows from their idiosyncratic source material.",
  },
  {
    id: 3,
    type: "blanks",
    question: "The historian's account, though (i)______ in its detail, is ultimately (ii)______: by focusing exclusively on the decisions of political elites, it (iii)______ the economic forces that shaped those decisions.",
    blanks: [
      { options: ["meticulous", "sparse", "fanciful"] },
      { options: ["incomplete", "exhaustive", "impartial"] },
      { options: ["neglects", "overstates", "illuminates"] },
    ],
    answer: ["meticulous", "incomplete", "neglects"],
    explanation: "\"Though\" contrasts a strength with a weakness: the account is meticulous in detail yet incomplete. The colon explains why — focusing only on elites, it neglects economic forces.",
  },
  {
    id: 4,
    type: "blanks",
    question: "Scientists had long assumed that deep-sea vent communities were (i)______, but recent surveys reveal a surprising (ii)______ of species, many of them previously unknown.",
    blanks: [
      { options: ["sparse", "teeming", "stable"] },
      { options: ["profusion", "dearth", "uniformity"] },
    ],
    answer: ["sparse", "profusion"],
    explanation: "\"But\" and \"surprising\" reverse the old assumption. Discovering many previously unknown species points to a profusion (abundance), so scientists had assumed the communities were sparse.",
  },
  {
    id: 5,
    type: "multi",
    selectCount: 2,
    instruction: "Select the two answer choices that, when used to complete the sentence, fit the meaning of the sentence as a whole and produce completed sentences that are alike in meaning.",
    question: "The committee's report was so ______ that even its supporters struggled to identify any concrete recommendations.",
    options: ["nebulous", "incisive", "vague", "trenchant", "comprehensive", "lengthy"],
    answer: ["nebulous", "vague"],
    explanation: "If supporters could not find concrete recommendations, the report lacked clarity: nebulous and vague. Incisive and trenchant are a synonym pair with the opposite meaning, and lengthy or comprehensive would not by themselves explain the lack of concrete content.",
  },
  {
    id: 6,
    type: "multi",
    selectCount: 2,
    instruction: "Select the two answer choices that, when used to complete the sentence, fit the meaning of the sentence as a whole and produce completed sentences that are alike in meaning.",
    question: "Despite his reputation for ______, the CEO surprised shareholders by approving a costly expansion with almost no deliberation.",
    options: ["prudence", "circumspection", "recklessness", "extravagance", "charisma", "impulsiveness"],
    answer: ["prudence", "circumspection"],
    explanation: "\"Despite\" and \"surprised\" mean his reputation contrasted with a hasty decision, so he was known for caution: prudence and circumspection. Recklessness and impulsiveness form a tempting pair but remove the contrast.",
  },
  {
    id: 7,
    type: "multi",
    selectCount: 2,
    instruction: "Select the two answer choices that, when used to complete the sentence, fit the meaning of the sentence as a whole and produce completed sentences that are alike in meaning.",
    question: "The professor's ______ remarks, delivered without notes and seemingly on a whim, nonetheless proved to be the most memorable part of the symposium.",
    options: ["extemporaneous", "rehearsed", "impromptu", "tedious", "scripted", "laconic"],
    answer: ["extemporaneous", "impromptu"],
    explanation: "Remarks delivered \"without notes and seemingly on a whim\" are unprepared: extemporaneous and impromptu. Rehearsed and scripted contradict the description.",
  },
  {
    id: 8,
    passage: FOREST_PASSAGE,
    question: "The primary purpose of the passage is to",
    options: [
      "describe a change in how ecologists understand the role of disturbance in forests and note a consequence of that change",
      "argue that controlled burns should replace all other forms of forest management",
      "explain why pine forests are more vulnerable to fire than hardwood forests are",
      "defend the concept of a climax community against recent criticism",
      "compare the risks of controlled burns with the risks of fire suppression",
    ],
    answer: "describe a change in how ecologists understand the role of disturbance in forests and note a consequence of that change",
    explanation: "Paragraph 1 traces the shift from the climax-community view to seeing disturbance as integral; paragraph 2 notes its practical consequence (controlled burns). The other choices are too extreme, too narrow, or reverse the author's position.",
  },
  {
    id: 9,
    type: "multi",
    passage: FOREST_PASSAGE,
    question: "According to the passage, which of the following is true of periodic low-intensity fires in some pine forests?",
    options: [
      "They reduce competition from hardwood seedlings.",
      "They contribute to the release of seeds from certain cones.",
      "They prevent all subsequent high-intensity fires.",
    ],
    answer: [
      "They reduce competition from hardwood seedlings.",
      "They contribute to the release of seeds from certain cones.",
    ],
    explanation: "The passage says these fires \"remove competing hardwood seedlings and release seeds from serotinous cones.\" It says suppressing them can lead to a more destructive fire, but never claims they prevent all intense fires.",
  },
  {
    id: 10,
    passage: FOREST_PASSAGE,
    question: "The author mentions the critics in the second paragraph most likely in order to",
    options: [
      "acknowledge reservations about a practice while maintaining that the broader shift in understanding is sound",
      "suggest that the older view of forest succession was largely correct",
      "demonstrate that controlled burns are rarely used in practice",
      "argue that the historical frequency of fire can be determined precisely",
      "introduce evidence that undisturbed forests are healthier than managed ones",
    ],
    answer: "acknowledge reservations about a practice while maintaining that the broader shift in understanding is sound",
    explanation: "The critics raise risks and uncertainty about controlled burns, but the next sentence (\"Yet few ecologists...\") reaffirms the new view. The author concedes a point without abandoning the main claim.",
  },
  {
    id: 11,
    passage: PRESS_PASSAGE,
    question: "The author's attitude toward the claim that the printing press accelerated literacy can best be described as",
    options: ["qualified acceptance", "unreserved endorsement", "outright rejection", "amused indifference", "reluctant hostility"],
    answer: "qualified acceptance",
    explanation: "The author argues literacy was already rising and the press partly responded to demand, yet insists this does not diminish the press's significance in sustaining and broadening the trend: acceptance with a qualification.",
  },
  {
    id: 12,
    passage: PRESS_PASSAGE,
    question: "Which of the following, if true, would most strengthen the author's suggestion that the press responded to an existing demand for reading material?",
    options: [
      "Printers established shops earlier in regions where commerce grew rapidly than in regions where commerce stagnated.",
      "Many of the earliest printed books were religious texts.",
      "Literacy rates continued to rise in some regions after the press was introduced.",
      "The cost of producing manuscripts by hand rose during the fifteenth century.",
      "Some merchants kept their accounts in numerical notation rather than in writing.",
    ],
    answer: "Printers established shops earlier in regions where commerce grew rapidly than in regions where commerce stagnated.",
    explanation: "If presses appeared first where commerce (and so literacy and demand) was already growing, that supports the idea that the press followed demand. The other choices are either neutral or consistent with the press creating demand.",
  },

  // ---------- SECTION 2 ----------

  {
    id: 13,
    question: "The biologist's findings, initially greeted with ______, gained acceptance only after independent laboratories replicated them.",
    options: ["skepticism", "acclaim", "enthusiasm", "alarm", "nostalgia"],
    answer: "skepticism",
    explanation: "Acceptance came \"only after\" replication, so the first reaction was doubt: skepticism. Acclaim and enthusiasm would mean the findings were already accepted.",
  },
  {
    id: 14,
    question: "Because the treaty's language was deliberately ______, each signatory could interpret its obligations in whatever way best suited its interests.",
    options: ["ambiguous", "precise", "punitive", "archaic", "binding"],
    answer: "ambiguous",
    explanation: "Language that lets each party read it however it likes is open to several interpretations: ambiguous. Precise wording would do the opposite.",
  },
  {
    id: 15,
    type: "blanks",
    question: "The architect's designs, once considered (i)______, now seem almost conventional, a sign not that her work has grown timid but that the profession has gradually (ii)______ her innovations.",
    blanks: [
      { options: ["radical", "derivative", "modest"] },
      { options: ["assimilated", "repudiated", "overlooked"] },
    ],
    answer: ["radical", "assimilated"],
    explanation: "Designs that \"now seem almost conventional\" must once have seemed radical. They seem ordinary because the profession absorbed (assimilated) her innovations, not because her work became timid.",
  },
  {
    id: 16,
    type: "blanks",
    question: "To the casual observer, the negotiations appeared (i)______; in reality, the delegates had spent months (ii)______ in private, so that the public sessions served largely to (iii)______ agreements already reached.",
    blanks: [
      { options: ["spontaneous", "acrimonious", "protracted"] },
      { options: ["deliberating", "feuding", "idling"] },
      { options: ["ratify", "undermine", "postpone"] },
    ],
    answer: ["spontaneous", "deliberating", "ratify"],
    explanation: "\"In reality\" contrasts appearance with preparation: the talks looked spontaneous but followed months of private deliberating. Since agreements were already reached, the public sessions merely ratified them.",
  },
  {
    id: 17,
    type: "blanks",
    question: "Her critique was hardly (i)______: rather than merely listing the study's flaws, she proposed specific methodological remedies that later researchers (ii)______.",
    blanks: [
      { options: ["carping", "measured", "novel"] },
      { options: ["adopted", "derided", "ignored"] },
    ],
    answer: ["carping", "adopted"],
    explanation: "\"Rather than merely listing flaws\" tells us the critique was not petty fault-finding, so it was hardly carping. Her remedies were constructive, and the positive tone implies later researchers adopted them.",
  },
  {
    id: 18,
    type: "multi",
    selectCount: 2,
    instruction: "Select the two answer choices that, when used to complete the sentence, fit the meaning of the sentence as a whole and produce completed sentences that are alike in meaning.",
    question: "Though the documentary purports to be ______, its selective editing reveals a clear bias toward one side of the controversy.",
    options: ["impartial", "partisan", "evenhanded", "polemical", "exhaustive", "dramatic"],
    answer: ["impartial", "evenhanded"],
    explanation: "\"Though\" contrasts the claim with the revealed bias, so the documentary claims to be unbiased: impartial and evenhanded. Partisan and polemical describe what it actually is.",
  },
  {
    id: 19,
    type: "multi",
    selectCount: 2,
    instruction: "Select the two answer choices that, when used to complete the sentence, fit the meaning of the sentence as a whole and produce completed sentences that are alike in meaning.",
    question: "The once-thriving port town had become so ______ that only a handful of residents remained to tend its empty streets.",
    options: ["desolate", "bustling", "deserted", "picturesque", "prosperous", "isolated"],
    answer: ["desolate", "deserted"],
    explanation: "Empty streets and only a handful of residents describe an abandoned place: desolate and deserted. Isolated refers to remoteness, not emptiness, and has no matching partner.",
  },
  {
    id: 20,
    type: "multi",
    selectCount: 2,
    instruction: "Select the two answer choices that, when used to complete the sentence, fit the meaning of the sentence as a whole and produce completed sentences that are alike in meaning.",
    question: "Critics accused the minister of ______, noting that she had publicly praised the very policy she had privately worked to defeat.",
    options: ["duplicity", "candor", "hypocrisy", "incompetence", "frankness", "timidity"],
    answer: ["duplicity", "hypocrisy"],
    explanation: "Praising a policy in public while working against it in private is two-faced behavior: duplicity and hypocrisy. Candor and frankness are a pair with the opposite meaning.",
  },
  {
    id: 21,
    type: "multi",
    selectCount: 2,
    instruction: "Select the two answer choices that, when used to complete the sentence, fit the meaning of the sentence as a whole and produce completed sentences that are alike in meaning.",
    question: "Rather than resolving the dispute, the court's ruling only served to ______ the tensions between the two communities.",
    options: ["exacerbate", "alleviate", "aggravate", "document", "conceal", "mollify"],
    answer: ["exacerbate", "aggravate"],
    explanation: "\"Rather than resolving\" means the ruling made things worse: exacerbate and aggravate. Alleviate and mollify are the opposite of what the sentence requires.",
  },
  {
    id: 22,
    passage: BEE_PASSAGE,
    question: "The passage is primarily concerned with",
    options: [
      "presenting evidence that a behavior once thought to serve a single function has broader consequences",
      "describing the mechanisms honeybees use to cool their nests",
      "refuting the hypothesis that colonies adjust brood temperature to their needs",
      "comparing the learning abilities of honeybees with those of other insects",
      "explaining why larvae cannot survive outside a narrow temperature range",
    ],
    answer: "presenting evidence that a behavior once thought to serve a single function has broader consequences",
    explanation: "Thermoregulation was assumed only to protect larvae; rearing experiments show it also shapes adult abilities. Cooling mechanisms are a detail, and the author calls the adjustment hypothesis speculative rather than refuting it.",
  },
  {
    id: 23,
    type: "multi",
    passage: BEE_PASSAGE,
    question: "According to the passage, worker bees can lower the temperature of the brood nest by which of the following?",
    options: [
      "Fanning",
      "Spreading water droplets that evaporate",
      "Contracting their flight muscles without moving their wings",
    ],
    answer: ["Fanning", "Spreading water droplets that evaporate"],
    explanation: "Workers \"lower it by fanning or by spreading water droplets.\" Contracting flight muscles is how they raise the temperature.",
  },
  {
    id: 24,
    passage: BEE_PASSAGE,
    question: "The author regards the hypothesis that colonies might adjust brood temperature to produce workers suited to different tasks as",
    options: [
      "plausible but not yet supported by direct evidence",
      "conclusively demonstrated by the rearing experiments",
      "inconsistent with the finding that bees raised at lower temperatures learn poorly",
      "irrelevant to understanding thermoregulation",
      "disproven by studies of the waggle dance",
    ],
    answer: "plausible but not yet supported by direct evidence",
    explanation: "The author calls the hypothesis \"speculative\" because no study has shown colonies vary nest temperature to meet changing needs. That is neither endorsement nor refutation.",
  },
  {
    id: 25,
    passage: BEE_PASSAGE,
    question: "It can be inferred from the passage that the rearing experiments differed from earlier assumptions about brood temperature in that they",
    options: [
      "examined the effects of temperature variation within the range colonies normally maintain",
      "were the first studies to measure brood nest temperature",
      "showed that temperatures outside 34–36 degrees Celsius are lethal",
      "observed colonies in the wild rather than in the laboratory",
      "focused on adult bees rather than on developing pupae",
    ],
    answer: "examined the effects of temperature variation within the range colonies normally maintain",
    explanation: "Earlier thinking concerned \"lethal extremes\"; the experiments reared pupae \"at slightly different temperatures within the normal range.\" The other choices are not supported or contradict the passage.",
  },
  {
    id: 26,
    passage: LIGHTING_PASSAGE,
    question: "Which of the following, if true, most seriously weakens the officials' conclusion?",
    options: [
      "During the same five years, reported nighttime burglaries fell by roughly 30 percent in nearby towns that did not change their street lighting.",
      "The brighter streetlights consume less electricity than the old ones.",
      "Some downtown business owners installed security cameras before the new lighting was installed.",
      "Residential neighborhoods in Larkfield have historically had fewer burglaries than the downtown district.",
      "Brighter lighting makes it easier for police patrols to spot suspicious activity.",
    ],
    answer: "During the same five years, reported nighttime burglaries fell by roughly 30 percent in nearby towns that did not change their street lighting.",
    explanation: "If similar towns without new lighting saw the same drop, some other regional factor likely caused the decline, undercutting the claim that lighting was responsible. Choice E strengthens the argument.",
  },
  {
    id: 27,
    passage: LETTERS_PASSAGE,
    question: "Which of the following best describes the function of the final sentence of the passage?",
    options: [
      "It draws a conclusion about a scholarly practice from the observation made in the preceding sentence.",
      "It offers an example that illustrates the claim made in the first sentence.",
      "It concedes that the practice described in the first sentence has some merit.",
      "It introduces a topic unrelated to the one discussed earlier.",
      "It restates, without comment, the view that the author rejects.",
    ],
    answer: "It draws a conclusion about a scholarly practice from the observation made in the preceding sentence.",
    explanation: "\"Therefore\" signals a conclusion: because letters are constructed for an audience (sentence 2), reading them as unmediated confession ignores their artistry. That conclusion criticizes the practice described in sentence 1.",
  },
];

export default verbalQuestions;
